import { environment } from "../../config/environment";
import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { Input } from "../ui/Input";
import { customFetch } from "../../utils/Auth";
import PatientsTimeline from "./PatientsTimeline";
import PatientsTable from "./PatientsTable";
import { InsightsTimeline, fetchInsightsByPatient } from "../Insights";
import { Toaster } from "../ui/Toaster";
import labelMappings from "../Trace/labels.json";
import vizConfig from "./viz-config.json";

const formatLocalDateTime = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const formatYYYYMMDD = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const eventKey = (e) => `${e?.patient_id ?? ""}::${e?.timestamp ?? ""}`;

const mergeEvents = (existing, incoming) => {
  const map = new Map();
  for (const e of existing || []) map.set(eventKey(e), e);
  for (const e of incoming || []) map.set(eventKey(e), e);
  return Array.from(map.values());
};

const countUniquePatients = (evts) => {
  const unique = new Set((evts || []).map((e) => String(e.patient_id || "")));
  unique.delete("");
  return unique.size;
};

const defaultLookbackHours =
  typeof vizConfig?.defaultLookbackHours === "number"
    ? vizConfig.defaultLookbackHours
    : 4;
const eventsPageSize =
  typeof vizConfig?.eventsPageSize === "number" ? vizConfig.eventsPageSize : 5000;
const lookbackOptions = Array.isArray(vizConfig?.lookbackOptionsHours)
  ? vizConfig.lookbackOptionsHours
  : [2, 4, 8, 12];

export default function EngineeringDashboardPage() {
  const today = useMemo(() => new Date(), []);
  const [date, setDate] = useState(formatYYYYMMDD(today));
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [events, setEvents] = useState([]);
  const [displayedPatients, setDisplayedPatients] = useState(0); // unique patients shown
  const [windowInfo, setWindowInfo] = useState(null);
  const [view, setView] = useState("timeline"); // 'timeline' | 'table'
  const [sortMode, setSortMode] = useState("latency"); // 'latency' | 'chronological' | 'chronologicalDesc'
  const [lookbackHours, setLookbackHours] = useState(defaultLookbackHours);
  const [fullDay, setFullDay] = useState(false);
  const [pagination, setPagination] = useState({ hasMore: false, nextCursor: null });
  const [loadingMore, setLoadingMore] = useState(false);
  // Shared insights dialog state (used by both views)
  const [insightsDialogOpen, setInsightsDialogOpen] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState(null);
  const [insightsLogs, setInsightsLogs] = useState([]);
  const [searchPid, setSearchPid] = useState("");
  const [filterPids, setFilterPids] = useState([]); // exact-match patient id filters
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [enabledSuppressed, setEnabledSuppressed] = useState([]); // names enabled from suppressed list
  // Table sections (from viz-config)
  const tableSections = Array.isArray(vizConfig?.tableSections) ? vizConfig.tableSections : [];
  const [tableSection, setTableSection] = useState(
    tableSections.length > 0 ? tableSections[0].name : ""
  );

  // Robustly convert various "errors" encodings to a list of strings
  const toStringList = (val) => {
    const norm = (arr) =>
      arr
        .map((v) => {
          if (typeof v === "string") return v.trim();
          if (v && typeof v === "object") {
            const obj = v;
            if (typeof obj.S === "string") return obj.S.trim();
            try {
              return JSON.stringify(obj);
            } catch {
              return String(obj);
            }
          }
          return String(v).trim();
        })
        .filter((s) => s.length > 0);
    try {
      if (Array.isArray(val)) return norm(val);
      if (typeof val === "string") {
        const t = val.trim();
        if (!t) return [];
        try {
          const parsed = JSON.parse(t);
          if (Array.isArray(parsed)) return norm(parsed);
        } catch {
          // not JSON array; treat as single string
        }
        return t === "[]" ? [] : [t];
      }
      return [];
    } catch {
      return [];
    }
  };

  // Build filtered events list based on exact-match patient id filters
  const filteredEvents = useMemo(() => {
    if (!Array.isArray(events)) return [];
    const base = events;
    // Precompute patients that have errors (any event with non-empty errors list)
    const patientsWithErrors = new Set();
    if (errorsOnly) {
      for (const e of base) {
        const pid = String(e?.patient_id ?? "");
        if (!pid) continue;
        if (toStringList(e?.errors).length > 0) {
          patientsWithErrors.add(pid);
        }
      }
    }
    // Filter by exact patient ID chips (if any)
    const hasPidFilters = Array.isArray(filterPids) && filterPids.length > 0;
    const wanted = hasPidFilters ? new Set(filterPids.map((p) => String(p))) : null;
    return base.filter((e) => {
      const pid = String(e?.patient_id ?? "");
      if (!pid) return false;
      if (hasPidFilters && !wanted.has(pid)) return false;
      if (errorsOnly && !patientsWithErrors.has(pid)) return false;
      return true;
    });
  }, [events, filterPids, errorsOnly]);

  const addFilterPid = (pidInput) => {
    const v = String(pidInput || "").trim();
    if (!v) return;
    setFilterPids((prev) => (prev.includes(v) ? prev : [...prev, v]));
  };

  // Compute actively suppressed names (from global labels.json only, viz-config suppression removed)
  const suppressedFromLabels = Array.isArray(labelMappings)
    ? labelMappings
        .filter((m) => m && typeof m === "object" && m.suppress === true && typeof m.original === "string")
        .map((m) => m.original)
    : [];
  const allDefaultSuppressed = useMemo(() => {
    const set = new Set((suppressedFromLabels || []).map(String));
    return Array.from(set);
  }, [suppressedFromLabels]);
  const activeSuppressed = useMemo(() => {
    const enabled = new Set((enabledSuppressed || []).map(String));
    return allDefaultSuppressed.filter((n) => !enabled.has(String(n)));
  }, [enabledSuppressed, allDefaultSuppressed]);

  const toggleEnableSuppressed = (name) => {
    setEnabledSuppressed((prev) => {
      const s = new Set(prev);
      if (s.has(name)) {
        s.delete(name);
      } else {
        s.add(name);
      }
      return Array.from(s);
    });
  };

  const removeFilterPid = (pid) => {
    setFilterPids((prev) => prev.filter((p) => p !== pid));
  };

  const shiftDateBy = (days) => {
    try {
      const [y, m, d] = date.split("-").map((s) => parseInt(s, 10));
      const dt = new Date(y, (m || 1) - 1, d || 1);
      dt.setDate(dt.getDate() + days);
      setDate(formatYYYYMMDD(dt));
    } catch {
      // noop
    }
  };

  const openPatientInsights = async (patientId) => {
    setSelectedPatientId(patientId);
    setInsightsDialogOpen(true);
    setInsightsLoading(true);
    setInsightsError(null);
    setInsightsLogs([]);
    try {
      const result = await fetchInsightsByPatient(patientId);
      if (result.error) {
        throw new Error(result.error);
      }
      setInsightsLogs(result.data);
    } catch (e) {
      setInsightsError(e?.message || "Failed to load insights");
    } finally {
      setInsightsLoading(false);
    }
  };

  const closePatientInsights = () => {
    setInsightsDialogOpen(false);
    setSelectedPatientId(null);
    setInsightsLogs([]);
    setInsightsError(null);
  };

  const buildFetchBody = (overrides = {}) => {
    const body = {
      date,
      tz: (vizConfig && vizConfig.timezone) || "America/Los_Angeles",
      lookbackHours,
      limit: eventsPageSize,
      fullDay,
      ...(vizConfig && vizConfig.testMode
        ? {
            testMode: true,
            testRecordsCount:
              typeof vizConfig.testRecordsCount === "number"
                ? vizConfig.testRecordsCount
                : undefined,
          }
        : {}),
      ...overrides,
    };
    return body;
  };

  const fetchEventsForDate = async () => {
    setLoading(true);
    setErrorMessage("");
    setPagination({ hasMore: false, nextCursor: null });
    try {
      const resp = await customFetch(
        `${environment.apiUrl}/engineering/events-by-date`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: localStorage.token,
          },
          body: JSON.stringify(buildFetchBody()),
        }
      );
      if (!resp.ok) {
        throw new Error(`Error: ${resp.status} ${resp.statusText}`);
      }
      const data = await resp.json();
      setWindowInfo(data.window || null);
      setPagination(data.pagination || { hasMore: false, nextCursor: null });
      const evts = data.events || [];
      setEvents(evts);
      setDisplayedPatients(countUniquePatients(evts));
    } catch (e) {
      setErrorMessage(e.message || "Failed to load data");
      setEvents([]);
      setDisplayedPatients(0);
      setWindowInfo(null);
      setPagination({ hasMore: false, nextCursor: null });
    } finally {
      setLoading(false);
    }
  };

  const loadMoreEvents = async () => {
    if (!pagination?.hasMore || !pagination?.nextCursor || loadingMore) return;
    setLoadingMore(true);
    setErrorMessage("");
    try {
      const resp = await customFetch(
        `${environment.apiUrl}/engineering/events-by-date`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: localStorage.token,
          },
          body: JSON.stringify(
            buildFetchBody({
              cursor: pagination.nextCursor,
              startTimeMs: windowInfo?.startTime,
              endTimeMs: windowInfo?.endTime,
            })
          ),
        }
      );
      if (!resp.ok) {
        throw new Error(`Error: ${resp.status} ${resp.statusText}`);
      }
      const data = await resp.json();
      setPagination(data.pagination || { hasMore: false, nextCursor: null });
      const evts = data.events || [];
      setEvents((prev) => {
        const merged = mergeEvents(prev, evts);
        setDisplayedPatients(countUniquePatients(merged));
        return merged;
      });
    } catch (e) {
      setErrorMessage(e.message || "Failed to load more data");
    } finally {
      setLoadingMore(false);
    }
  };

  const loadEarlierWindow = async () => {
    if (!windowInfo || loadingMore || fullDay) return;
    const dayStart = windowInfo.dayStartTime ?? windowInfo.startTime;
    const currentStart = windowInfo.startTime;
    if (typeof dayStart !== "number" || typeof currentStart !== "number") return;
    if (currentStart <= dayStart) return;

    const chunkMs = lookbackHours * 60 * 60 * 1000;
    const newEnd = currentStart;
    const newStart = Math.max(dayStart, newEnd - chunkMs);
    if (newEnd <= newStart) return;

    setLoadingMore(true);
    setErrorMessage("");
    try {
      const resp = await customFetch(
        `${environment.apiUrl}/engineering/events-by-date`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: localStorage.token,
          },
          body: JSON.stringify(
            buildFetchBody({
              startTimeMs: newStart,
              endTimeMs: newEnd,
            })
          ),
        }
      );
      if (!resp.ok) {
        throw new Error(`Error: ${resp.status} ${resp.statusText}`);
      }
      const data = await resp.json();
      const evts = data.events || [];
      setEvents((prev) => {
        const merged = mergeEvents(evts, prev);
        setDisplayedPatients(countUniquePatients(merged));
        return merged;
      });
      setWindowInfo((prev) =>
        prev ? { ...prev, startTime: newStart } : data.window || null
      );
    } catch (e) {
      setErrorMessage(e.message || "Failed to load earlier data");
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchEventsForDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, lookbackHours, fullDay]);

  return (
    <div className="flex flex-col w-full">
      <NavLink
        to={"/"}
        className="absolute top-[1rem] left-[2rem] text-ktx-purple text-[20px] hover:text-ktx-navy font-semibold"
      >
        Back
      </NavLink>
      <div className="text-3xl text-light-grey tracking-wider mb-4 w-full">
        <div className={`p-10 ${loading ? "pointer-events-none" : ""}`}>
          <div className="p-4 space-y-4">
            {/* Centered header + date controls */}
            <div className="flex flex-col items-center">
              {/* Page title */}
              <div className="flex flex-col items-center gap-2 mb-2">
                <img
                  src="/argus.png"
                  alt="Argus logo"
                  className="w-32 h-32 md:w-40 md:h-40 rounded-md ring-1 ring-neutral-300 object-contain"
                />
                <h1 className="text-2xl md:text-3xl font-bold text-neutral-900 tracking-wide">
                  Argus
                </h1>
              </div>
              {/* Date controls */}
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  aria-label="Previous day"
                  title="Previous day"
                  onClick={() => shiftDateBy(-1)}
                  className="px-3 py-2 rounded-md border text-xl md:text-2xl leading-none bg-white hover:bg-neutral-50 text-neutral-700"
                  disabled={loading}
                >
                  ‹
                </button>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="p-2 border rounded-md w-44 md:w-56"
                />
                <button
                  type="button"
                  aria-label="Next day"
                  title="Next day"
                  onClick={() => shiftDateBy(1)}
                  className="px-3 py-2 rounded-md border text-xl md:text-2xl leading-none bg-white hover:bg-neutral-50 text-neutral-700"
                  disabled={loading}
                >
                  ›
                </button>
              </div>
            </div>
            {/* View toggle (left) and Sort (right) */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={`px-3 py-1 rounded-md border text-sm ${view === "timeline" ? "bg-neutral-900 text-white" : "bg-white text-neutral-800"}`}
                  onClick={() => setView("timeline")}
                >
                  Timeline
                </button>
                <button
                  type="button"
                  className={`px-3 py-1 rounded-md border text-sm ${view === "table" ? "bg-neutral-900 text-white" : "bg-white text-neutral-800"}`}
                  onClick={() => setView("table")}
                >
                  Table
                </button>
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <label className="text-sm text-neutral-600">Window</label>
                  <select
                    value={fullDay ? "full" : String(lookbackHours)}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "full") {
                        setFullDay(true);
                      } else {
                        setFullDay(false);
                        setLookbackHours(parseInt(v, 10));
                      }
                    }}
                    className="border rounded-md px-2 py-1 text-sm"
                    disabled={loading}
                  >
                    {lookbackOptions.map((h) => (
                      <option key={`lb-${h}`} value={String(h)}>
                        Last {h}h
                      </option>
                    ))}
                    <option value="full">Full day (slow)</option>
                  </select>
                  <label className="text-sm text-neutral-600">Sort</label>
                  <select
                    value={sortMode}
                    onChange={(e) => setSortMode(e.target.value)}
                    className="border rounded-md px-2 py-1 text-sm"
                  >
                    <option value="latency">Latency (longest first)</option>
                    <option value="chronologicalDesc">Chronological (latest first)</option>
                    <option value="chronological">Chronological (earliest first)</option>
                  </select>
                </div>
                <button
                  type="button"
                  aria-label="Refresh data"
                  title="Refresh data"
                  onClick={fetchEventsForDate}
                  className="p-1 rounded-md border bg-white hover:bg-neutral-50 text-neutral-700"
                  disabled={loading}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 12a9 9 0 1 1-3-6.7" />
                    <polyline points="21 3 21 9 15 9" />
                  </svg>
                </button>
                
                {loading ? (
                  <div className="text-xs text-neutral-500 mt-1">Loading…</div>
                ) : null}
              </div>
            </div>
            {/* Patient search (placed below view/sort controls) */}
            <div className="flex flex-col items-start justify-start mt-3">
              <label htmlFor="patient-search" className="text-sm text-neutral-600 mb-1">
                Filter patient ids
              </label>
              <input
                id="patient-search"
                type="text"
                value={searchPid}
                onChange={(e) => setSearchPid(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addFilterPid(searchPid);
                    setSearchPid("");
                  }
                }}
                placeholder="Type an id and press Enter"
                className="border rounded-md px-3 py-2 text-sm w-64"
              />
              {/* Active filters as removable chips + clear-all */}
              <div className="flex flex-wrap gap-2 mt-2">
                {filterPids.map((pid) => (
                  <span
                    key={`pid-chip-${pid}`}
                    className="inline-flex items-center gap-1 bg-neutral-100 text-neutral-800 border border-neutral-300 rounded-full px-2 py-0.5 text-xs"
                  >
                    <span>{pid}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${pid}`}
                      className="text-neutral-500 hover:text-neutral-800"
                      onClick={() => removeFilterPid(pid)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {filterPids.length > 0 && (
                  <button
                    type="button"
                    className="text-xs text-red-600 hover:text-red-700 underline"
                    onClick={() => setFilterPids([])}
                    title="Clear all filters"
                  >
                    Clear all ×
                  </button>
                )}
              </div>
              {/* Show errors only toggle */}
              <label className="mt-2 inline-flex items-center gap-2 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  checked={errorsOnly}
                  onChange={(e) => setErrorsOnly(e.target.checked)}
                />
                <span>Show errors only</span>
              </label>
            </div>
          </div>

          {errorMessage && (
            <div className="mt-5 bg-red-50 p-4 rounded-md">
              <h4 className="text-lg font-semibold text-red-800">Error:</h4>
              <p className="text-sm text-red-700 mt-2">{errorMessage}</p>
            </div>
          )}

          <div className="mt-8">
            {events && events.length > 0 ? (
              <>
                {view === "timeline" ? (
                  <PatientsTimeline
                    events={filteredEvents}
                    sortMode={sortMode}
                    onOpenPatientTrace={openPatientInsights}
                    suppressedNames={activeSuppressed}
                  />
                ) : (
                  <>
                    {Array.isArray(vizConfig?.tableSections) && vizConfig.tableSections.length > 0 && (
                      <div className="mb-3 flex items-center gap-2">
                        {vizConfig.tableSections.map((s) => (
                          <button
                            key={s.name}
                            type="button"
                            className={`px-3 py-1 rounded-md border text-sm ${
                              tableSection === s.name
                                ? "bg-neutral-900 text-white"
                                : "bg-white text-neutral-800"
                            }`}
                            onClick={() => setTableSection(s.name)}
                          >
                            {s.name}
                          </button>
                        ))}
                      </div>
                    )}
                    <PatientsTable
                      events={filteredEvents}
                      sortMode={sortMode}
                      onOpenPatientTrace={openPatientInsights}
                      activeSection={tableSection}
                    />
                  </>
                )}
                <div className="mt-4 flex flex-col gap-2 text-xs text-neutral-600">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      {windowInfo?.startTime && windowInfo?.endTime ? (
                        <span>
                          Window:{" "}
                          {formatLocalDateTime(new Date(windowInfo.startTime))} →{" "}
                          {formatLocalDateTime(new Date(windowInfo.endTime))}
                          {fullDay ? " (full day)" : ` (last ${lookbackHours}h)`}
                          {" · "}
                          {events.length.toLocaleString()} events · {countUniquePatients(filteredEvents)} patients
                        </span>
                      ) : (
                        <span>
                          Showing {countUniquePatients(filteredEvents)} patients
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {!fullDay &&
                        windowInfo?.dayStartTime != null &&
                        windowInfo.startTime > windowInfo.dayStartTime && (
                          <button
                            type="button"
                            onClick={loadEarlierWindow}
                            disabled={loading || loadingMore}
                            className="px-2 py-1 rounded border bg-white hover:bg-neutral-50 text-neutral-800 disabled:opacity-50"
                          >
                            {loadingMore ? "Loading…" : `Load earlier ${lookbackHours}h`}
                          </button>
                        )}
                      {pagination?.hasMore && (
                        <button
                          type="button"
                          onClick={loadMoreEvents}
                          disabled={loading || loadingMore}
                          className="px-2 py-1 rounded border bg-white hover:bg-neutral-50 text-neutral-800 disabled:opacity-50"
                        >
                          {loadingMore ? "Loading…" : "Load more events"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              !loading && (
                <div className="text-sm text-neutral-500">
                  No events found for {date}.
                </div>
              )
            )}
          </div>
        </div>
      </div>
      {/* Shared insights dialog */}
      {insightsDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-[95vw] max-w-5xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="font-semibold text-sm">
                Insights – {selectedPatientId}
              </div>
              <button
                type="button"
                onClick={closePatientInsights}
                className="text-sm text-neutral-600 hover:text-neutral-900"
              >
                Close
              </button>
            </div>
            <div className="p-4">
              {insightsLoading && (
                <div className="text-sm text-neutral-600">Loading…</div>
              )}
              {insightsError && (
                <div className="text-sm text-red-600">{insightsError}</div>
              )}
              {!insightsLoading && !insightsError && insightsLogs.length === 0 && (
                <div className="text-sm text-neutral-600">
                  No insight events found.
                </div>
              )}
              {!insightsLoading && !insightsError && insightsLogs.length > 0 && (
                <div className="mt-2">
                  <InsightsTimeline data={insightsLogs} labelMappings={labelMappings} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Full-page loading overlay */}
      {loading && (
        <div
          className="fixed inset-0 z-[60] bg-white/60 backdrop-blur-sm flex items-center justify-center"
          aria-busy="true"
          aria-live="polite"
        >
          <div className="flex flex-col items-center gap-3">
            <div className="h-12 w-12 rounded-full border-4 border-ktx-purple border-t-transparent animate-spin" />
            <div className="text-sm text-neutral-700">Loading…</div>
          </div>
        </div>
      )}
      <Toaster />
    </div>
  );
}

